/**
 * Éditeur d'image style Instagram :
 * - Filtres (vintage, N&B, sépia, lumineux, frais, chaud)
 * - Ajustements (luminosité, contraste, saturation)
 * - Recadrage (1:1, 4:5, 9:16)
 * - Stickers (emoji, texte, GIF) draggables
 * - Export Canvas vers Blob (1080x1080 ou 1080x1350 ou 1080x1920)
 */
import { useCallback, useEffect, useRef, useState } from "react";
import Cropper from "react-easy-crop";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Trash2, Type, Sparkles, SlidersHorizontal, Crop as CropIcon, Layers, Bold, Italic, X } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EmojiPickerPopover } from "./EmojiPickerPopover";
import { GiphyPickerPopover } from "./GiphyPickerPopover";
import brandLogoTransparent from "@/assets/brand-logo-transparent.png";
import brandNameTransparent from "@/assets/brand-name-transparent.png";
import brandBaselineTransparent from "@/assets/brand-baseline-transparent.png";

const BRAND_STICKERS = [
  { id: 'brand-logo', label: '🌿 Logo Regard de Nature', url: brandLogoTransparent, aspect: 1 },
  { id: 'brand-name', label: '✨ "Regard de Nature" (texte)', url: brandNameTransparent, aspect: 421 / 138 },
  { id: 'brand-baseline', label: '✨ "Naturopathie & Iridologie"', url: brandBaselineTransparent, aspect: 421 / 36 },
];

export type FilterPreset = 'none' | 'vintage' | 'bw' | 'sepia' | 'bright' | 'cool' | 'warm' | 'fade' | 'vivid';
export type AspectRatio = '1:1' | '4:5' | '9:16';
export type TextStyle =
  | 'classic' | 'modern' | 'signature' | 'editor' | 'poster' | 'bubble'
  | 'deco' | 'squeeze' | 'typewriter' | 'strong' | 'meme' | 'elegant'
  | 'directional' | 'literature' | 'script'
  | 'neon' | 'pop' | 'jump' | 'sparkle' | 'shimmer' | 'pixel';
export type TextAnimation = 'none' | 'typewriter' | 'pop' | 'jump' | 'sparkle' | 'neon' | 'shimmer' | 'pixel';

export interface ImageSticker {
  id: string;
  type: 'emoji' | 'text' | 'gif' | 'image' | 'poll';
  content: string; // emoji char, text content, gif url, or image url
  previewContent?: string;
  x: number;
  y: number;
  size: number;
  rotation: number;
  color?: string;
  bgColor?: string;
  fontWeight?: 'normal' | 'bold';
  fontStyle?: 'normal' | 'italic';
  fontFamily?: string;
  textStyle?: TextStyle;
  textAnimation?: TextAnimation;
  imageShape?: 'circle' | 'polaroid' | 'rounded' | 'star' | 'original';
  imageAspect?: number; // width / height — used for 'original' shape to preserve transparency
  // poll-specific
  pollQuestion?: string;
  pollOptions?: string[];
}

export interface ImageEditorState {
  filter: FilterPreset;
  brightness: number; // 0-200, 100 = neutral
  contrast: number;
  saturation: number;
  aspectRatio: AspectRatio;
  crop: { x: number; y: number };
  zoom: number;
  croppedAreaPixels: { x: number; y: number; width: number; height: number } | null;
  stickers: ImageSticker[];
}

export const DEFAULT_EDITOR_STATE: ImageEditorState = {
  filter: 'none',
  brightness: 100,
  contrast: 100,
  saturation: 100,
  aspectRatio: '1:1',
  crop: { x: 0, y: 0 },
  zoom: 1,
  croppedAreaPixels: null,
  stickers: [],
};

export const FILTER_PRESETS: Record<FilterPreset, { label: string; css: string }> = {
  none: { label: 'Original', css: '' },
  vintage: { label: 'Vintage', css: 'sepia(0.4) contrast(1.1) saturate(1.2)' },
  bw: { label: 'N&B', css: 'grayscale(1) contrast(1.1)' },
  sepia: { label: 'Sépia', css: 'sepia(1)' },
  bright: { label: 'Lumineux', css: 'brightness(1.15) saturate(1.15)' },
  cool: { label: 'Frais', css: 'hue-rotate(-15deg) saturate(1.1)' },
  warm: { label: 'Chaud', css: 'sepia(0.2) saturate(1.3) hue-rotate(-10deg)' },
  fade: { label: 'Délavé', css: 'contrast(0.85) saturate(0.7) brightness(1.1)' },
  vivid: { label: 'Vif', css: 'saturate(1.6) contrast(1.15)' },
};

const ASPECT_MAP: Record<AspectRatio, number> = { '1:1': 1, '4:5': 4 / 5, '9:16': 9 / 16 };

/** Instagram-like text styles. fontFamily must be loaded in index.html. */
export const TEXT_STYLES: Record<TextStyle, {
  label: string;
  fontFamily: string;
  cssTextShadow?: string;
  defaultColor?: string;
  defaultBg?: string;
  uppercase?: boolean;
  letterSpacing?: string;
}> = {
  // Instagram-like text styles
  classic:     { label: 'Classique',   fontFamily: '"Montserrat", sans-serif', cssTextShadow: '0 2px 6px rgba(0,0,0,0.7)' },
  modern:      { label: 'Moderne',     fontFamily: '"Bebas Neue", "Montserrat", sans-serif', cssTextShadow: '0 2px 8px rgba(0,0,0,0.6)', uppercase: true, letterSpacing: '0.04em' },
  signature:   { label: 'Signature',   fontFamily: '"Sacramento", "Pacifico", cursive', cssTextShadow: '0 2px 6px rgba(0,0,0,0.5)' },
  editor:      { label: 'Editor',      fontFamily: '"Playfair Display", serif', cssTextShadow: '0 2px 6px rgba(0,0,0,0.6)' },
  poster:      { label: 'Poster',      fontFamily: '"Alfa Slab One", "Bungee", serif', cssTextShadow: '0 3px 8px rgba(0,0,0,0.7)', uppercase: true },
  bubble:      { label: 'Bubble',      fontFamily: '"Bowlby One", "Fredoka", sans-serif', defaultColor: '#ffffff', defaultBg: '#ff2d55', uppercase: true, letterSpacing: '0.02em' },
  deco:        { label: 'Deco',        fontFamily: '"Bungee Shade", "Bungee", sans-serif', cssTextShadow: '0 2px 6px rgba(0,0,0,0.5)', uppercase: true },
  squeeze:     { label: 'Squeeze',     fontFamily: '"Rubik Mono One", "Bungee", sans-serif', cssTextShadow: '0 2px 6px rgba(0,0,0,0.6)', uppercase: true, letterSpacing: '-0.02em' },
  typewriter:  { label: 'Typewriter',  fontFamily: '"Special Elite", "Courier New", monospace', cssTextShadow: '0 1px 2px rgba(0,0,0,0.5)' },
  strong:      { label: 'Strong',      fontFamily: '"Anton", "Bebas Neue", sans-serif', cssTextShadow: '0 2px 6px rgba(0,0,0,0.7)', uppercase: true, letterSpacing: '0.02em' },
  meme:        { label: 'Meme',        fontFamily: 'Impact, "Anton", sans-serif', defaultColor: '#ffffff', cssTextShadow: '-2px -2px 0 #000, 2px -2px 0 #000, -2px 2px 0 #000, 2px 2px 0 #000', uppercase: true },
  elegant:     { label: 'Elegant',     fontFamily: '"Cinzel", serif', cssTextShadow: '0 2px 6px rgba(0,0,0,0.6)', letterSpacing: '0.06em' },
  directional: { label: 'Directional', fontFamily: '"Righteous", "Bebas Neue", sans-serif', cssTextShadow: '0 2px 6px rgba(0,0,0,0.5)', uppercase: true, letterSpacing: '0.04em' },
  literature:  { label: 'Literature',  fontFamily: '"Abril Fatface", "Playfair Display", serif', cssTextShadow: '0 2px 6px rgba(0,0,0,0.6)' },
  script:      { label: 'Manuscrite',  fontFamily: '"Caveat", cursive', cssTextShadow: '0 2px 4px rgba(0,0,0,0.5)' },
  // Effect / animated styles (kept for backwards compat — selecting these auto-applies the matching animation)
  neon:        { label: 'Néon',        fontFamily: '"Permanent Marker", cursive', defaultColor: '#ff2bd6', cssTextShadow: '0 0 6px #ff2bd6, 0 0 14px #ff2bd6, 0 0 28px #ff007a' },
  pop:         { label: 'Pop',         fontFamily: '"Bungee", sans-serif', defaultColor: '#ffffff', defaultBg: '#ff2d55', uppercase: true, letterSpacing: '0.02em' },
  jump:        { label: 'Jump',        fontFamily: '"Fredoka", "Bungee", sans-serif', defaultColor: '#ffffff', cssTextShadow: '0 2px 6px rgba(0,0,0,0.6)', uppercase: true },
  sparkle:     { label: 'Étincelle',   fontFamily: '"Pacifico", cursive', defaultColor: '#fff7b0', cssTextShadow: '0 0 10px #fff5a8, 0 0 20px #ffd700, 0 2px 4px rgba(0,0,0,0.4)' },
  shimmer:     { label: 'Chatoiement', fontFamily: '"Dancing Script", cursive', defaultColor: '#ffe9a8', cssTextShadow: '0 0 12px rgba(255,215,0,0.7), 0 2px 6px rgba(0,0,0,0.5)' },
  pixel:       { label: 'Pixel',       fontFamily: '"Press Start 2P", monospace', defaultColor: '#00ff88', cssTextShadow: '2px 2px 0 #003322' },
};

/** Maps a style preset to its companion animation when one applies. */
export const STYLE_ANIMATION_MAP: Partial<Record<TextStyle, TextAnimation>> = {
  neon: 'neon', pop: 'pop', jump: 'jump', sparkle: 'sparkle',
  shimmer: 'shimmer', pixel: 'pixel', typewriter: 'typewriter',
};

export const TEXT_ANIMATIONS: Record<TextAnimation, { label: string; className?: string }> = {
  none:       { label: 'Aucune' },
  typewriter: { label: 'Typewriter', className: 'ig-anim-typewriter' },
  pop:        { label: 'Pop',        className: 'ig-anim-pop' },
  jump:       { label: 'Jump',       className: 'ig-anim-jump' },
  sparkle:    { label: 'Étincelle',  className: 'ig-anim-sparkle' },
  neon:       { label: 'Néon',       className: 'ig-anim-neon' },
  shimmer:    { label: 'Chatoiement', className: 'ig-anim-shimmer' },
  pixel:      { label: 'Pixel',      className: 'ig-anim-pixel' },
};


interface Props {
  imageUrl: string;
  state: ImageEditorState;
  onChange: (s: ImageEditorState) => void;
}

export function ImageEditor({ imageUrl, state, onChange }: Props) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const [pendingImageShape, setPendingImageShape] = useState<NonNullable<ImageSticker['imageShape']>>('circle');
  const stateRef = useRef(state);
  // Map sticker id -> DOM element, for direct transform updates during drag (no re-render)
  const stickerNodes = useRef<Map<string, HTMLDivElement>>(new Map());
  // Local position overrides while dragging (pct). Drained on pointerup.
  const livePos = useRef<Map<string, { x: number; y: number }>>(new Map());

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const update = useCallback((patch: Partial<ImageEditorState>) => {
    const next = { ...stateRef.current, ...patch };
    stateRef.current = next;
    onChange(next);
  }, [onChange]);

  const buildFilter = useCallback(() => {
    const preset = FILTER_PRESETS[state.filter].css;
    const adj = `brightness(${state.brightness}%) contrast(${state.contrast}%) saturate(${state.saturation}%)`;
    return `${preset} ${adj}`.trim();
  }, [state.filter, state.brightness, state.contrast, state.saturation]);

  const onCropComplete = useCallback((_a: any, areaPixels: any) => {
    update({ croppedAreaPixels: areaPixels });
  }, [update]);

  const addSticker = useCallback((s: Omit<ImageSticker, 'id' | 'x' | 'y' | 'rotation' | 'size'>) => {
    const sticker: ImageSticker = {
      id: crypto.randomUUID(),
      x: 50,
      y: 50,
      size: s.type === 'text' ? 64 : s.type === 'emoji' ? 120 : s.type === 'poll' ? 360 : s.type === 'image' ? 320 : 240,
      rotation: 0,
      ...s,
      color: s.color || '#ffffff',
    };
    update({ stickers: [...stateRef.current.stickers, sticker] });
  }, [update]);

  const removeSticker = useCallback((id: string) => {
    update({ stickers: stateRef.current.stickers.filter(s => s.id !== id) });
  }, [update]);

  const updateSticker = useCallback((id: string, patch: Partial<ImageSticker>) => {
    update({ stickers: stateRef.current.stickers.map(s => s.id === id ? { ...s, ...patch } : s) });
  }, [update]);

  // Smooth drag: update DOM transform directly via rAF; commit to state only on pointerup.
  const startDrag = useCallback((e: React.PointerEvent<HTMLDivElement>, id: string) => {
    e.preventDefault();
    e.stopPropagation();
    const overlay = overlayRef.current;
    if (!overlay) return;
    const target = e.currentTarget;
    target.setPointerCapture?.(e.pointerId);
    const sticker = stateRef.current.stickers.find(s => s.id === id);
    const rotation = sticker?.rotation ?? 0;

    let pendingX = sticker?.x ?? 50;
    let pendingY = sticker?.y ?? 50;
    let rafId = 0;
    let dirty = false;

    const apply = () => {
      rafId = 0;
      dirty = false;
      const node = stickerNodes.current.get(id);
      if (!node) return;
      node.style.left = `${pendingX}%`;
      node.style.top = `${pendingY}%`;
    };

    const onMove = (ev: PointerEvent) => {
      ev.preventDefault();
      const rect = overlay.getBoundingClientRect();
      const x = ((ev.clientX - rect.left) / rect.width) * 100;
      const y = ((ev.clientY - rect.top) / rect.height) * 100;
      pendingX = Math.max(0, Math.min(100, x));
      pendingY = Math.max(0, Math.min(100, y));
      livePos.current.set(id, { x: pendingX, y: pendingY });
      if (!dirty) {
        dirty = true;
        rafId = requestAnimationFrame(apply);
      }
    };

    const stop = () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', stop);
      window.removeEventListener('pointercancel', stop);
      livePos.current.delete(id);
      // Commit final position to React state
      updateSticker(id, { x: pendingX, y: pendingY });
      void rotation;
    };

    window.addEventListener('pointermove', onMove, { passive: false });
    window.addEventListener('pointerup', stop);
    window.addEventListener('pointercancel', stop);
  }, [updateSticker]);

  return (
    <div className="space-y-3 sm:space-y-4">
      <div className="grid lg:grid-cols-[1fr_320px] gap-3 sm:gap-4">
        {/* Aperçu / zone d'édition */}
        <div className="space-y-2">
          <div
            className="relative isolate w-full max-w-[520px] mx-auto bg-muted rounded-lg overflow-hidden border"
            style={{ aspectRatio: ASPECT_MAP[state.aspectRatio] }}
          >
            <div className="absolute inset-0 z-0">
              <Cropper
                image={imageUrl}
                crop={state.crop}
                zoom={state.zoom}
                aspect={ASPECT_MAP[state.aspectRatio]}
                onCropChange={(c) => update({ crop: c })}
                onZoomChange={(z) => update({ zoom: z })}
                onCropComplete={onCropComplete}
                style={{
                  containerStyle: { zIndex: 0 },
                  mediaStyle: { filter: buildFilter() },
                  cropAreaStyle: { zIndex: 1 },
                }}
                objectFit="cover"
                showGrid={false}
              />
            </div>
            {/* Overlay stickers (drag) */}
            <div
              ref={overlayRef}
              className="absolute inset-0 z-30 pointer-events-none"
              style={{ transform: 'translateZ(0)', WebkitTransform: 'translateZ(0)' }}
            >
              {state.stickers.map((s) => {
                const previewWidth = overlayRef.current?.clientWidth || 320;
                const scale = previewWidth / 1080;
                const sizeStyle = `${s.size * scale}px`;
                return (
                  <div
                    key={s.id}
                    ref={(el) => {
                      if (el) stickerNodes.current.set(s.id, el);
                      else stickerNodes.current.delete(s.id);
                    }}
                    className="absolute pointer-events-auto cursor-move select-none group"
                    style={{
                      left: `${s.x}%`,
                      top: `${s.y}%`,
                      position: 'absolute',
                      transform: `translate(-50%, -50%) rotate(${s.rotation}deg)`,
                      touchAction: 'none',
                      zIndex: 2,
                      willChange: 'left, top, transform',
                      WebkitTransform: `translate3d(-50%, -50%, 0) rotate(${s.rotation}deg)`,
                    }}
                    onPointerDown={(e) => startDrag(e, s.id)}
                  >
                    {s.type === 'emoji' && (
                      <span style={{ fontSize: sizeStyle, lineHeight: 1, display: 'inline-block', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.4))' }}>
                        {s.content}
                      </span>
                    )}
                    {s.type === 'text' && (() => {
                      const ts = TEXT_STYLES[s.textStyle || 'classic'];
                      const display = ts.uppercase ? s.content.toUpperCase() : s.content;
                      const anim = s.textAnimation || STYLE_ANIMATION_MAP[s.textStyle || 'classic'] || 'none';
                      const animClass = TEXT_ANIMATIONS[anim].className || '';
                      const isShimmer = anim === 'shimmer';
                      return (
                        <span
                          className={animClass}
                          style={{
                            fontSize: sizeStyle,
                            color: isShimmer ? undefined : (s.color || ts.defaultColor || '#ffffff'),
                            backgroundColor: s.bgColor || ts.defaultBg || 'transparent',
                            padding: (s.bgColor || ts.defaultBg) ? '0.15em 0.5em' : 0,
                            borderRadius: (s.bgColor || ts.defaultBg) ? '0.35em' : 0,
                            fontWeight: s.fontWeight,
                            fontStyle: s.fontStyle,
                            fontFamily: s.fontFamily || ts.fontFamily,
                            letterSpacing: ts.letterSpacing,
                            textShadow: anim === 'sparkle' || anim === 'neon' ? undefined : (ts.cssTextShadow || '0 2px 6px rgba(0,0,0,0.7)'),
                            whiteSpace: 'pre',
                            lineHeight: 1.15,
                            display: 'inline-block',
                          }}
                        >
                          {display}
                        </span>
                      );
                    })()}
                    {s.type === 'gif' && (
                      <img
                        src={s.previewContent || s.content}
                        alt=""
                        style={{ width: sizeStyle, height: 'auto', display: 'block', filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.4))', pointerEvents: 'none' }}
                        onError={(e) => {
                          if (s.previewContent && e.currentTarget.src !== s.content) {
                            e.currentTarget.src = s.content;
                          }
                        }}
                        draggable={false}
                      />
                    )}
                    {s.type === 'image' && (() => {
                      const w = parseFloat(sizeStyle);
                      const shape = s.imageShape || 'circle';
                      if (shape === 'polaroid') {
                        return (
                          <div style={{
                            width: `${w}px`, padding: `${w * 0.04}px ${w * 0.04}px ${w * 0.18}px`,
                            background: '#fff', boxShadow: '0 6px 18px rgba(0,0,0,0.35)',
                          }}>
                            <img src={s.content} alt="" draggable={false}
                              style={{ width: '100%', height: `${w * 0.92}px`, objectFit: 'cover', display: 'block' }} />
                          </div>
                        );
                      }
                      if (shape === 'original') {
                        const aspect = s.imageAspect || 1;
                        return (
                          <img src={s.content} alt="" draggable={false}
                            style={{
                              width: `${w}px`, height: `${w / aspect}px`,
                              objectFit: 'contain', display: 'block',
                              filter: 'drop-shadow(0 4px 10px rgba(0,0,0,0.35))',
                            }} />
                        );
                      }
                      const radius = shape === 'circle' ? '50%' : shape === 'rounded' ? '12%' : '0';
                      const clipPath = shape === 'star'
                        ? "polygon(50% 0%,61% 35%,98% 35%,68% 57%,79% 91%,50% 70%,21% 91%,32% 57%,2% 35%,39% 35%)"
                        : undefined;
                      return (
                        <img src={s.content} alt="" draggable={false}
                          style={{
                            width: `${w}px`, height: `${w}px`, objectFit: 'cover',
                            borderRadius: radius, clipPath,
                            border: shape === 'circle' || shape === 'rounded' ? `${Math.max(2, w * 0.025)}px solid #fff` : undefined,
                            boxShadow: '0 6px 18px rgba(0,0,0,0.4)',
                            display: 'block',
                          }} />
                      );
                    })()}
                    {s.type === 'poll' && (() => {
                      const w = parseFloat(sizeStyle);
                      const opts = s.pollOptions || ['Oui', 'Non'];
                      return (
                        <div style={{
                          width: `${w}px`, background: 'rgba(255,255,255,0.96)', borderRadius: `${w * 0.08}px`,
                          padding: `${w * 0.08}px`, boxShadow: '0 8px 24px rgba(0,0,0,0.35)', textAlign: 'center',
                          fontFamily: '"Montserrat", sans-serif',
                        }}>
                          <div style={{ fontSize: `${w * 0.09}px`, fontWeight: 700, color: '#111', marginBottom: `${w * 0.06}px` }}>
                            {s.pollQuestion || s.content || 'Posez une question'}
                          </div>
                          <div style={{ display: 'flex', gap: `${w * 0.04}px` }}>
                            {opts.slice(0, 2).map((opt, i) => (
                              <div key={i} style={{
                                flex: 1, padding: `${w * 0.05}px 0`, borderRadius: `${w * 0.05}px`,
                                background: i === 0 ? 'hsl(var(--primary))' : 'hsl(var(--secondary))',
                                color: i === 0 ? 'white' : '#111', fontSize: `${w * 0.07}px`, fontWeight: 600,
                              }}>{opt}</div>
                            ))}
                          </div>
                        </div>
                      );
                    })()}
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); removeSticker(s.id); }}
                      className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full w-5 h-5 flex items-center justify-center text-[10px] opacity-0 group-hover:opacity-100"
                      aria-label="Supprimer"
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground text-center">
            Glissez pour recadrer • Faites glisser les stickers pour les déplacer
          </p>
        </div>

        {/* Panneau d'options */}
        <Tabs defaultValue="crop" className="w-full">
          <TabsList className="grid grid-cols-4 w-full h-auto p-1 sticky top-0 z-10 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 border">
            <TabsTrigger value="crop" className="flex flex-col gap-0.5 py-2 text-[10px]"><CropIcon className="w-4 h-4" /><span>Recadrer</span></TabsTrigger>
            <TabsTrigger value="filters" className="flex flex-col gap-0.5 py-2 text-[10px]"><Sparkles className="w-4 h-4" /><span>Filtres</span></TabsTrigger>
            <TabsTrigger value="adjust" className="flex flex-col gap-0.5 py-2 text-[10px]"><SlidersHorizontal className="w-4 h-4" /><span>Réglages</span></TabsTrigger>
            <TabsTrigger value="stickers" className="flex flex-col gap-0.5 py-2 text-[10px]"><Layers className="w-4 h-4" /><span>Stickers</span></TabsTrigger>
          </TabsList>

          {/* Crop */}
          <TabsContent value="crop" className="space-y-3 pt-3">
            <div className="space-y-2">
              <Label>Format</Label>
              <div className="grid grid-cols-3 gap-2">
                {(['1:1', '4:5', '9:16'] as AspectRatio[]).map((r) => (
                  <Button
                    key={r}
                    type="button"
                    variant={state.aspectRatio === r ? "default" : "outline"}
                    size="sm"
                    onClick={() => update({ aspectRatio: r })}
                  >
                    {r}
                  </Button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label className="flex justify-between">Zoom <span className="text-xs text-muted-foreground">{state.zoom.toFixed(1)}x</span></Label>
              <Slider value={[state.zoom]} min={1} max={3} step={0.05} onValueChange={(v) => update({ zoom: v[0] })} />
            </div>
          </TabsContent>

          {/* Filters */}
          <TabsContent value="filters" className="pt-3">
            <div className="grid grid-cols-4 sm:grid-cols-3 gap-2 max-h-[320px] overflow-y-auto">
              {(Object.keys(FILTER_PRESETS) as FilterPreset[]).map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => update({ filter: f })}
                  className={`flex flex-col items-center gap-1 p-1.5 rounded border text-xs transition ${state.filter === f ? 'border-primary bg-primary/10' : 'hover:border-primary/50'}`}
                >
                  <div
                    className="w-full aspect-square rounded overflow-hidden bg-muted"
                    style={{
                      backgroundImage: `url(${imageUrl})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      filter: FILTER_PRESETS[f].css,
                    }}
                  />
                  {FILTER_PRESETS[f].label}
                </button>
              ))}
            </div>
          </TabsContent>

          {/* Adjust */}
          <TabsContent value="adjust" className="space-y-4 pt-3">
            {([
              ['Luminosité', 'brightness'],
              ['Contraste', 'contrast'],
              ['Saturation', 'saturation'],
            ] as const).map(([label, key]) => (
              <div key={key} className="space-y-2">
                <Label className="flex justify-between text-xs">
                  {label}
                  <span className="text-muted-foreground">{state[key]}%</span>
                </Label>
                <Slider
                  value={[state[key]]}
                  min={0} max={200} step={1}
                  onValueChange={(v) => update({ [key]: v[0] } as any)}
                />
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" className="w-full"
              onClick={() => update({ brightness: 100, contrast: 100, saturation: 100, filter: 'none' })}>
              Réinitialiser
            </Button>
          </TabsContent>

          {/* Stickers */}
          <TabsContent value="stickers" className="space-y-3 pt-3">
            {/* Hidden file input for Photo / Décors / Avatar uploads */}
            <input
              ref={photoInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.currentTarget.value = '';
                if (!file) return;
                const reader = new FileReader();
                reader.onload = () => {
                  const url = reader.result as string;
                  addSticker({ type: 'image', content: url, imageShape: pendingImageShape });
                };
                reader.readAsDataURL(file);
              }}
            />

            <div className="flex flex-wrap gap-2">
              <EmojiPickerPopover onSelect={(e) => addSticker({ type: 'emoji', content: e })} triggerLabel="Emoji" />
              <Button type="button" variant="outline" size="sm" className="gap-1.5"
                onClick={() => addSticker({ type: 'text', content: 'Votre texte', color: '#ffffff', fontWeight: 'bold', textStyle: 'classic' })}>
                <Type className="w-4 h-4" /> Texte
              </Button>
              <GiphyPickerPopover onSelect={(g) => addSticker({ type: 'gif', content: g.url, previewContent: g.editorStillUrl || g.editorUrl || g.preview || g.url })} />
            </div>

            {/* Templates style Instagram — dropdown */}
            <div>
              <Label className="text-[10px] uppercase text-muted-foreground">Stickers prêts à l'emploi</Label>
              <Select
                value=""
                onValueChange={(val) => {
                  switch (val) {
                    case 'location':  addSticker({ type: 'text', content: '📍 Lieu', color: '#7c3aed', fontWeight: 'bold', textStyle: 'pop' }); break;
                    case 'mention':   addSticker({ type: 'text', content: '@mention', color: '#ffffff', fontWeight: 'bold', textStyle: 'modern' }); break;
                    case 'hashtag':   addSticker({ type: 'text', content: '#hashtag', color: '#ec4899', fontWeight: 'bold', textStyle: 'pop' }); break;
                    case 'link':      addSticker({ type: 'text', content: '🔗 Lien', color: '#0ea5e9', fontWeight: 'bold', textStyle: 'pop' }); break;
                    case 'question':  addSticker({ type: 'text', content: 'Posez-moi une question', color: '#ffffff', fontWeight: 'bold', textStyle: 'pop' }); break;
                    case 'countdown': addSticker({ type: 'text', content: '⏱ J-7', color: '#ffffff', fontWeight: 'bold', textStyle: 'neon' }); break;
                    case 'music':     addSticker({ type: 'text', content: '🎵 Titre — Artiste', color: '#ffffff', fontWeight: 'bold', textStyle: 'modern' }); break;
                    case 'photo':     setPendingImageShape('polaroid'); photoInputRef.current?.click(); break;
                    case 'decor':     setPendingImageShape('rounded'); photoInputRef.current?.click(); break;
                    case 'avatar':    setPendingImageShape('circle'); photoInputRef.current?.click(); break;
                    case 'featured':  setPendingImageShape('star'); photoInputRef.current?.click(); break;
                    case 'poll':      addSticker({ type: 'poll', content: '', pollQuestion: 'Votre avis ?', pollOptions: ['Oui', 'Non'] } as any); break;
                    case 'donation':  addSticker({ type: 'text', content: '💛 Faire un don', color: '#ffffff', fontWeight: 'bold', textStyle: 'pop' }); break;
                    case 'guest':     addSticker({ type: 'text', content: '@invité', color: '#ffffff', fontWeight: 'bold', textStyle: 'modern' }); break;
                    default:
                      if (val.startsWith('brand:')) {
                        const id = val.slice(6);
                        const b = BRAND_STICKERS.find(x => x.id === id);
                        if (b) {
                          addSticker({ type: 'image', content: b.url, imageShape: 'original', imageAspect: b.aspect });
                        }
                      }
                      break;
                  }
                }}
              >
                <SelectTrigger className="h-9 mt-1 text-xs">
                  <SelectValue placeholder="Choisir un sticker…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="location">📍 Localisation</SelectItem>
                  <SelectItem value="mention">@ Mention</SelectItem>
                  <SelectItem value="hashtag"># Hashtag</SelectItem>
                  <SelectItem value="link">🔗 Lien</SelectItem>
                  <SelectItem value="question">❓ Question</SelectItem>
                  <SelectItem value="countdown">⏱ Compte à rebours</SelectItem>
                  <SelectItem value="music">🎵 Musique</SelectItem>
                  <SelectItem value="photo">🖼 Photo (Polaroïd)</SelectItem>
                  <SelectItem value="decor">📷 Décors</SelectItem>
                  <SelectItem value="avatar">👤 Avatar</SelectItem>
                  <SelectItem value="featured">⭐ À la une</SelectItem>
                  <SelectItem value="poll">🗳 Sondage</SelectItem>
                  <SelectItem value="donation">💛 Don</SelectItem>
                  <SelectItem value="guest">➕ Ajout perso</SelectItem>
                  <SelectItem value="guest">➕ Ajout perso</SelectItem>
                  {BRAND_STICKERS.map(b => (
                    <SelectItem key={b.id} value={`brand:${b.id}`}>{b.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Branding direct buttons */}
            <div>
              <Label className="text-[10px] uppercase text-muted-foreground">Mon branding</Label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                {BRAND_STICKERS.map(b => (
                  <button
                    key={b.id}
                    type="button"
                    onClick={() => addSticker({ type: 'image', content: b.url, imageShape: 'original', imageAspect: b.aspect })}
                    className="flex items-center justify-center p-2 rounded border bg-background hover:border-primary/60 transition aspect-square"
                    title={b.label}
                  >
                    <img src={b.url} alt={b.label} className="max-w-full max-h-full object-contain" draggable={false} />
                  </button>
                ))}
              </div>
            </div>


            {state.stickers.length > 0 && (
              <div className="space-y-2 max-h-[260px] overflow-y-auto">
                {state.stickers.map((s) => (
                  <Card key={s.id} className="p-2 space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-medium truncate">
                        {s.type === 'emoji' && <>Emoji {s.content}</>}
                        {s.type === 'text' && <>Texte: {s.content.slice(0, 20)}</>}
                        {s.type === 'gif' && <>GIF</>}
                        {s.type === 'image' && <>Image ({s.imageShape || 'circle'})</>}
                        {s.type === 'poll' && <>Sondage</>}
                      </span>
                      <Button type="button" variant="ghost" size="icon" className="h-6 w-6"
                        onClick={() => removeSticker(s.id)}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                    {s.type === 'text' && (
                      <>
                        <Input value={s.content} onChange={(e) => updateSticker(s.id, { content: e.target.value })} className="h-7 text-xs" />
                        <div>
                          <Label className="text-[10px]">Style de police</Label>
                          <Select
                            value={s.textStyle || 'classic'}
                            onValueChange={(val) => updateSticker(s.id, { textStyle: val as TextStyle, fontFamily: undefined, color: undefined, bgColor: undefined })}
                          >
                            <SelectTrigger className="h-8 mt-1 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="max-h-[260px]">
                              {(Object.keys(TEXT_STYLES) as TextStyle[]).map((ts) => (
                                <SelectItem key={ts} value={ts}>
                                  <span style={{ fontFamily: TEXT_STYLES[ts].fontFamily }}>{TEXT_STYLES[ts].label}</span>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-[10px]">Animation</Label>
                          <Select
                            value={s.textAnimation || STYLE_ANIMATION_MAP[s.textStyle || 'classic'] || 'none'}
                            onValueChange={(val) => updateSticker(s.id, { textAnimation: val as TextAnimation })}
                          >
                            <SelectTrigger className="h-8 mt-1 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {(Object.keys(TEXT_ANIMATIONS) as TextAnimation[]).map((a) => (
                                <SelectItem key={a} value={a}>{TEXT_ANIMATIONS[a].label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="flex items-center gap-2">
                          <div className="flex flex-col items-center gap-0.5">
                            <Label className="text-[9px]">Texte</Label>
                            <input type="color" value={s.color || '#ffffff'} onChange={(e) => updateSticker(s.id, { color: e.target.value })}
                              className="w-7 h-7 rounded border cursor-pointer" />
                          </div>
                          <div className="flex flex-col items-center gap-0.5">
                            <Label className="text-[9px]">Fond</Label>
                            <input type="color" value={s.bgColor || '#000000'} onChange={(e) => updateSticker(s.id, { bgColor: e.target.value })}
                              className="w-7 h-7 rounded border cursor-pointer" />
                          </div>
                          <Button type="button" variant="ghost" size="icon" className="h-7 w-7" title="Retirer le fond"
                            onClick={() => updateSticker(s.id, { bgColor: undefined })}>
                            <X className="w-3 h-3" />
                          </Button>
                          <Button type="button" variant={s.fontWeight === 'bold' ? 'default' : 'outline'} size="icon" className="h-7 w-7"
                            onClick={() => updateSticker(s.id, { fontWeight: s.fontWeight === 'bold' ? 'normal' : 'bold' })}>
                            <Bold className="w-3 h-3" />
                          </Button>
                          <Button type="button" variant={s.fontStyle === 'italic' ? 'default' : 'outline'} size="icon" className="h-7 w-7"
                            onClick={() => updateSticker(s.id, { fontStyle: s.fontStyle === 'italic' ? 'normal' : 'italic' })}>
                            <Italic className="w-3 h-3" />
                          </Button>
                        </div>
                      </>
                    )}
                    {s.type === 'image' && (
                      <div className="flex flex-wrap gap-1">
                        {(['original', 'circle', 'rounded', 'polaroid', 'star'] as const).map((sh) => (
                          <Button key={sh} type="button" size="sm"
                            variant={(s.imageShape || 'circle') === sh ? 'default' : 'outline'}
                            className="h-7 px-2 text-[10px]"
                            onClick={() => updateSticker(s.id, { imageShape: sh })}>
                            {sh === 'original' ? 'Original' : sh === 'circle' ? 'Rond' : sh === 'rounded' ? 'Carré' : sh === 'polaroid' ? 'Polaroïd' : 'Étoile'}
                          </Button>
                        ))}
                      </div>
                    )}
                    {s.type === 'poll' && (
                      <div className="space-y-1.5">
                        <Input value={s.pollQuestion || ''} placeholder="Question"
                          onChange={(e) => updateSticker(s.id, { pollQuestion: e.target.value })}
                          className="h-7 text-xs" />
                        <div className="grid grid-cols-2 gap-1.5">
                          <Input value={(s.pollOptions || ['', ''])[0] || ''} placeholder="Option 1"
                            onChange={(e) => {
                              const opts = [...(s.pollOptions || ['', ''])];
                              opts[0] = e.target.value;
                              updateSticker(s.id, { pollOptions: opts });
                            }}
                            className="h-7 text-xs" />
                          <Input value={(s.pollOptions || ['', ''])[1] || ''} placeholder="Option 2"
                            onChange={(e) => {
                              const opts = [...(s.pollOptions || ['', ''])];
                              opts[1] = e.target.value;
                              updateSticker(s.id, { pollOptions: opts });
                            }}
                            className="h-7 text-xs" />
                        </div>
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-[10px]">Taille</Label>
                        <Slider value={[s.size]} min={32} max={400} step={2}
                          onValueChange={(v) => updateSticker(s.id, { size: v[0] })} />
                      </div>
                      <div>
                        <Label className="text-[10px]">Rotation</Label>
                        <Slider value={[s.rotation]} min={-180} max={180} step={1}
                          onValueChange={(v) => updateSticker(s.id, { rotation: v[0] })} />
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

/**
 * Génère le rendu final de l'image éditée (Blob JPEG/PNG) à partir de l'image source et de l'état.
 * Applique : crop, filtre CSS, stickers (emoji/text/gif).
 */
export async function renderEditedImage(
  imageUrl: string,
  state: ImageEditorState,
  outputSize: { width: number; height: number } = { width: 1080, height: 1080 }
): Promise<Blob | null> {
  const img = await loadImg(imageUrl);
  const canvas = document.createElement('canvas');
  canvas.width = outputSize.width;
  canvas.height = outputSize.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  // Apply filter
  const preset = FILTER_PRESETS[state.filter].css;
  const adj = `brightness(${state.brightness}%) contrast(${state.contrast}%) saturate(${state.saturation}%)`;
  ctx.filter = `${preset} ${adj}`.trim();

  // Crop
  if (state.croppedAreaPixels) {
    const c = state.croppedAreaPixels;
    ctx.drawImage(img, c.x, c.y, c.width, c.height, 0, 0, outputSize.width, outputSize.height);
  } else {
    ctx.drawImage(img, 0, 0, outputSize.width, outputSize.height);
  }
  ctx.filter = 'none';

  // Stickers (sized for 1080-base)
  const scale = outputSize.width / 1080;
  for (const s of state.stickers) {
    const cx = (s.x / 100) * outputSize.width;
    const cy = (s.y / 100) * outputSize.height;
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate((s.rotation * Math.PI) / 180);
    if (s.type === 'emoji') {
      ctx.font = `${s.size * scale}px "Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.shadowColor = 'rgba(0,0,0,0.5)';
      ctx.shadowBlur = 8;
      ctx.fillText(s.content, 0, 0);
    } else if (s.type === 'text') {
      const ts = TEXT_STYLES[s.textStyle || 'classic'];
      const display = ts.uppercase ? s.content.toUpperCase() : s.content;
      const fontFamily = s.fontFamily || ts.fontFamily;
      const fontParts = [
        s.fontStyle === 'italic' ? 'italic' : '',
        s.fontWeight === 'bold' ? 'bold' : '',
        `${s.size * scale}px`,
        fontFamily,
      ].filter(Boolean).join(' ');
      ctx.font = fontParts;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const fillColor = s.color || ts.defaultColor || '#ffffff';
      const bg = s.bgColor || ts.defaultBg;

      // Pill background
      if (bg) {
        const metrics = ctx.measureText(display);
        const w = metrics.width + s.size * scale * 0.5;
        const h = s.size * scale * 1.25;
        ctx.fillStyle = bg;
        const r = h * 0.25;
        const x = -w / 2, y = -h / 2;
        ctx.beginPath();
        ctx.moveTo(x + r, y);
        ctx.arcTo(x + w, y, x + w, y + h, r);
        ctx.arcTo(x + w, y + h, x, y + h, r);
        ctx.arcTo(x, y + h, x, y, r);
        ctx.arcTo(x, y, x + w, y, r);
        ctx.closePath();
        ctx.fill();
      }

      // Style-specific glow effects (multi-pass for neon/sparkle/shimmer)
      const style = s.textStyle || 'classic';
      const glowPasses: Array<{ color: string; blur: number }> = [];
      if (style === 'neon') {
        glowPasses.push({ color: fillColor, blur: 18 * scale }, { color: fillColor, blur: 32 * scale });
      } else if (style === 'sparkle') {
        glowPasses.push({ color: '#ffd700', blur: 24 * scale });
      } else if (style === 'shimmer') {
        glowPasses.push({ color: 'rgba(255,215,0,0.8)', blur: 22 * scale });
      } else if (style === 'pixel') {
        glowPasses.push({ color: 'rgba(0,0,0,0.9)', blur: 0 });
      } else {
        glowPasses.push({ color: 'rgba(0,0,0,0.7)', blur: 12 });
      }
      ctx.fillStyle = fillColor;
      for (const pass of glowPasses) {
        ctx.shadowColor = pass.color;
        ctx.shadowBlur = pass.blur;
        if (style === 'pixel') {
          ctx.shadowOffsetX = 2 * scale;
          ctx.shadowOffsetY = 2 * scale;
        }
        ctx.fillText(display, 0, 0);
      }
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 0;
    } else if (s.type === 'gif') {
      try {
        const g = await loadImg(s.previewContent || s.content);
        const w = s.size * scale;
        const h = (g.height / g.width) * w;
        ctx.drawImage(g, -w / 2, -h / 2, w, h);
      } catch {/* skip */}
    } else if (s.type === 'image') {
      try {
        const img2 = await loadImg(s.content);
        const w = s.size * scale;
        const shape = s.imageShape || 'circle';
        if (shape === 'original') {
          const aspect = s.imageAspect || (img2.width / img2.height) || 1;
          const h = w / aspect;
          ctx.save();
          ctx.shadowColor = 'rgba(0,0,0,0.35)'; ctx.shadowBlur = 12; ctx.shadowOffsetY = 4;
          ctx.drawImage(img2, -w / 2, -h / 2, w, h);
          ctx.restore();
        } else if (shape === 'polaroid') {
          const padding = w * 0.04;
          const bottomPad = w * 0.18;
          const totalH = w * 0.92 + padding + bottomPad;
          ctx.fillStyle = '#fff';
          ctx.shadowColor = 'rgba(0,0,0,0.35)'; ctx.shadowBlur = 18;
          ctx.fillRect(-w / 2, -totalH / 2, w, totalH);
          ctx.shadowBlur = 0;
          ctx.drawImage(img2, -w / 2 + padding, -totalH / 2 + padding, w - padding * 2, w * 0.92);
        } else {
          ctx.save();
          ctx.shadowColor = 'rgba(0,0,0,0.4)'; ctx.shadowBlur = 16;
          ctx.beginPath();
          if (shape === 'circle') {
            ctx.arc(0, 0, w / 2, 0, Math.PI * 2);
          } else if (shape === 'rounded') {
            const r = w * 0.12;
            ctx.moveTo(-w / 2 + r, -w / 2);
            ctx.arcTo(w / 2, -w / 2, w / 2, w / 2, r);
            ctx.arcTo(w / 2, w / 2, -w / 2, w / 2, r);
            ctx.arcTo(-w / 2, w / 2, -w / 2, -w / 2, r);
            ctx.arcTo(-w / 2, -w / 2, w / 2, -w / 2, r);
          } else if (shape === 'star') {
            const spikes = 5, outer = w / 2, inner = outer * 0.45;
            for (let i = 0; i < spikes * 2; i++) {
              const r = i % 2 === 0 ? outer : inner;
              const a = (Math.PI / spikes) * i - Math.PI / 2;
              if (i === 0) ctx.moveTo(Math.cos(a) * r, Math.sin(a) * r);
              else ctx.lineTo(Math.cos(a) * r, Math.sin(a) * r);
            }
          }
          ctx.closePath();
          ctx.clip();
          ctx.drawImage(img2, -w / 2, -w / 2, w, w);
          ctx.restore();
          if (shape === 'circle' || shape === 'rounded') {
            ctx.lineWidth = Math.max(2, w * 0.025);
            ctx.strokeStyle = '#fff';
            ctx.stroke();
          }
        }
      } catch {/* skip */}
    } else if (s.type === 'poll') {
      const w = s.size * scale;
      const opts = s.pollOptions || ['Oui', 'Non'];
      const padding = w * 0.08;
      const titleH = w * 0.18;
      const optH = w * 0.22;
      const totalH = padding * 2 + titleH + optH;
      ctx.save();
      ctx.shadowColor = 'rgba(0,0,0,0.35)'; ctx.shadowBlur = 24;
      ctx.fillStyle = 'rgba(255,255,255,0.96)';
      const r = w * 0.08;
      const x = -w / 2, y = -totalH / 2;
      ctx.beginPath();
      ctx.moveTo(x + r, y);
      ctx.arcTo(x + w, y, x + w, y + totalH, r);
      ctx.arcTo(x + w, y + totalH, x, y + totalH, r);
      ctx.arcTo(x, y + totalH, x, y, r);
      ctx.arcTo(x, y, x + w, y, r);
      ctx.closePath();
      ctx.fill();
      ctx.shadowBlur = 0;
      ctx.fillStyle = '#111';
      ctx.font = `bold ${w * 0.09}px "Montserrat", sans-serif`;
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText(s.pollQuestion || s.content || 'Posez une question', 0, y + padding + titleH / 2);
      const optY = y + padding + titleH;
      const gap = w * 0.04;
      const optW = (w - padding * 2 - gap) / 2;
      opts.slice(0, 2).forEach((opt, i) => {
        const ox = x + padding + i * (optW + gap);
        ctx.fillStyle = i === 0 ? '#CCA727' : '#506842';
        const orad = optH * 0.25;
        ctx.beginPath();
        ctx.moveTo(ox + orad, optY);
        ctx.arcTo(ox + optW, optY, ox + optW, optY + optH, orad);
        ctx.arcTo(ox + optW, optY + optH, ox, optY + optH, orad);
        ctx.arcTo(ox, optY + optH, ox, optY, orad);
        ctx.arcTo(ox, optY, ox + optW, optY, orad);
        ctx.closePath(); ctx.fill();
        ctx.fillStyle = '#fff';
        ctx.font = `600 ${w * 0.07}px "Montserrat", sans-serif`;
        ctx.fillText(opt, ox + optW / 2, optY + optH / 2);
      });
      ctx.restore();
    }
    ctx.restore();
  }

  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.92));
}

/**
 * Rend l'image éditée SANS les stickers GIF (utilisé pour composer une vidéo MP4 animée
 * où les GIFs sont overlayés en tant que vidéos animées via Cloudinary).
 * Retourne aussi la liste des stickers GIF avec leurs positions/tailles normalisées (px sur outputSize).
 */
export async function renderEditedImageWithoutGifs(
  imageUrl: string,
  state: ImageEditorState,
  outputSize: { width: number; height: number } = { width: 1080, height: 1080 }
): Promise<{ blob: Blob | null; gifs: Array<{ url: string; xPx: number; yPx: number; widthPx: number; heightPx: number; rotation: number }> }> {
  const stateNoGifs: ImageEditorState = {
    ...state,
    stickers: state.stickers.filter((s) => s.type !== 'gif'),
  };
  const blob = await renderEditedImage(imageUrl, stateNoGifs, outputSize);

  const scale = outputSize.width / 1080;
  const gifs: Array<{ url: string; xPx: number; yPx: number; widthPx: number; heightPx: number; rotation: number }> = [];

  for (const s of state.stickers) {
    if (s.type !== 'gif') continue;
    let widthPx = s.size * scale;
    let heightPx = widthPx; // default square if metadata not loadable
    try {
      const g = await loadImg(s.previewContent || s.content);
      heightPx = (g.height / g.width) * widthPx;
    } catch { /* keep square */ }
    const xPx = (s.x / 100) * outputSize.width;
    const yPx = (s.y / 100) * outputSize.height;
    gifs.push({
      url: s.content,
      xPx: Math.round(xPx),
      yPx: Math.round(yPx),
      widthPx: Math.round(widthPx),
      heightPx: Math.round(heightPx),
      rotation: s.rotation || 0,
    });
  }

  return { blob, gifs };
}

function loadImg(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const i = new Image();
    i.crossOrigin = 'anonymous';
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = src;
  });
}
